import React, { useEffect } from "react";
import { Outlet } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import BottomNavigation from "./BottomNavigation";
import background from "../assets/background.svg";

/**
 * Переиспользуемый Layout компонент с различными вариантами компоновки
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Дочерние элементы (альтернатива Outlet)
 * @param {string} props.variant - Вариант компоновки
 * @param {boolean} props.showBackground - Показывать фоновое изображение
 * @param {boolean} props.showHeader - Показывать header
 * @param {boolean} props.showFooter - Показывать footer
 * @param {boolean} props.showNavigation - Показывать нижнюю навигацию
 * @param {string} props.className - Дополнительные CSS классы
 * @param {Object} props.headerProps - Пропсы для Header компонента
 * @param {Object} props.footerProps - Пропсы для Footer компонента
 * @param {Object} props.navigationProps - Пропсы для BottomNavigation компонента
 */
const ReusableLayout = ({
  children,
  variant = "default",
  showBackground = false,
  showHeader = false,
  showFooter = false,
  showNavigation = false,
  className = "",
  headerProps = {},
  footerProps = {},
  navigationProps = {},
  ...props
}) => {
  // Глобальная инициализация Telegram Mini App полноэкранного режима
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;

      console.log(
        "📱 [Global] Настройка Telegram Mini App для полного экрана..."
      );

      // Расширяем на весь экран
      tg.expand();

      // Устанавливаем темные цвета
      if (tg.setHeaderColor) {
        tg.setHeaderColor("#010d0d");
      }

      if (tg.setBackgroundColor) {
        tg.setBackgroundColor("#010d0d");
      }

      // Отключаем вертикальные свайпы для лучшего UX
      if (tg.disableVerticalSwipes) {
        tg.disableVerticalSwipes();
      }

      // Скрываем главную кнопку по умолчанию
      if (tg.MainButton) {
        tg.MainButton.hide();
      }

      // Включаем тактильную обратную связь если доступно
      if (tg.HapticFeedback) {
        tg.HapticFeedback.selectionChanged();
      }

      console.log("✅ [Global] Telegram Mini App настроен для полного экрана");
    }
  }, []);

  // Базовые стили для разных вариантов
  const variants = {
    default: "flex flex-col justify-start items-center w-full h-full",
    centered: "flex flex-col justify-center items-center w-full h-full",
    fullscreen: "w-full h-full",
    "with-header": "flex flex-col w-full h-full",
    "with-footer": "flex flex-col w-full h-full",
    "with-navigation": "flex flex-col w-full h-full pb-20", // отступ для навигации
    page: "w-full h-full pt-4 flex flex-col font-raleway items-center overflow-x-hidden", // стиль как в Match.jsx
    match:
      "w-[100%] h-[100%] pt-[16px] flex flex-col font-raleway !items-center overflow-x-hidden", // точный стиль из Match.jsx
  };

  const containerClasses = `
    ${variants[variant] || variants.default}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div className={containerClasses} {...props}>
      {/* Фоновое изображение */}
      {showBackground && (
        <img
          src={background}
          alt="Background"
          className="absolute w-full bottom-[16px] z-0 pointer-events-none"
        />
      )}

      {/* Header */}
      {showHeader && <Header {...headerProps} />}

      {/* Основной контент */}
      <main className="flex flex-col w-full h-full overflow-auto relative z-10">
        {children || <Outlet />}
      </main>

      {/* Footer */}
      {showFooter && <Footer {...footerProps} />}

      {/* Нижняя навигация */}
      {showNavigation && <BottomNavigation {...navigationProps} />}
    </div>
  );
};

export default ReusableLayout;
